import { useEffect, useMemo, useState } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { api, isApiConfigured, type Job, type Usage } from './lib/api';
import { loadPrefs, type Strength, type Tone } from './lib/prefs';

const sample = `Artificial intelligence has become an increasingly important tool within higher education. It is important to note that students can utilise these technologies in order to improve the efficiency of their learning process. Furthermore, AI-powered systems can provide personalised feedback, identify knowledge gaps, and assist students in developing a deeper understanding of complex topics. However, it is crucial that universities establish clear guidelines to ensure these tools are used responsibly (Rahman, 2024).`;

function countWords(value: string) {
  const trimmed = value.trim();
  return trimmed ? trimmed.split(/\s+/).length : 0;
}

type StudioProps = {
  user: User;
  session: Session;
  usage: Usage | null;
  onJobsChange: (jobs: Job[]) => void;
  onUsageChange: (usage: Usage) => void;
  notify: (message: string) => void;
};

export function HumanizerStudio({ user: _user, session, usage, onJobsChange, onUsageChange, notify }: StudioProps) {
  const initial = loadPrefs();
  const [source, setSource] = useState('');
  const [result, setResult] = useState('');
  const [tone, setTone] = useState<Tone>(initial.tone);
  const [strength, setStrength] = useState<Strength>(initial.strength);
  const [guard, setGuard] = useState(initial.guard);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('Ready');
  const [activeJobId, setActiveJobId] = useState<string | null>(null);

  useEffect(() => {
    const prefs = loadPrefs();
    setTone(prefs.tone);
    setStrength(prefs.strength);
    setGuard(prefs.guard);
  }, []);

  const sourceMeta = useMemo(() => `${countWords(source)} words · ${source.length.toLocaleString()} characters`, [source]);
  const resultMeta = useMemo(() => `${countWords(result)} words`, [result]);
  const changeMeta = useMemo(() => {
    if (!result || !source) return '—';
    const change = Math.round(Math.abs(countWords(result) - countWords(source)) / countWords(source) * 100);
    return `${change}% length change`;
  }, [result, source]);

  useEffect(() => {
    if (!session || !activeJobId || !isApiConfigured()) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const job = await api.job(session, activeJobId);
        if (cancelled) return;
        setStatus(job.status === 'completed' ? 'Complete' : job.status === 'failed' ? 'Failed' : job.status[0].toUpperCase() + job.status.slice(1));
        if (job.output_text) setResult(job.output_text);
        if (job.status === 'completed' || job.status === 'failed') {
          setLoading(false);
          setActiveJobId(null);
          const [me, history] = await Promise.all([api.me(session), api.jobs(session)]);
          if (!cancelled) {
            onUsageChange(me.usage);
            onJobsChange(history.items);
          }
        }
      } catch (error) {
        if (!cancelled) {
          setActiveJobId(null);
          setStatus('Failed');
          notify(error instanceof Error ? error.message : 'The job could not be loaded.');
        }
      }
    };
    void poll();
    const timer = window.setInterval(() => { void poll(); }, 1800);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [activeJobId, notify, onJobsChange, onUsageChange, session]);

  const runHumanize = async () => {
    if (!source.trim()) {
      notify('Add a draft to get started');
      return;
    }
    if (!isApiConfigured()) {
      notify('The rewrite API is not configured yet.');
      return;
    }
    setLoading(true);
    setResult('');
    setStatus('Queued');
    try {
      const queued = await api.humanize(session, null, source);
      setActiveJobId(queued.job_id);
      notify('Queued securely · your use is reserved');
    } catch (error) {
      setLoading(false);
      setStatus('Failed');
      notify(error instanceof Error ? error.message : 'The rewrite could not be queued.');
    }
  };

  const copyResult = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result);
      notify('Copied to clipboard');
    } catch {
      notify('Select and copy the text manually');
    }
  };

  const downloadResult = () => {
    if (!result) return;
    const link = document.createElement('a');
    link.href = URL.createObjectURL(new Blob([result], { type: 'text/plain' }));
    link.download = 'phrasio-rewrite.txt';
    link.click();
    URL.revokeObjectURL(link.href);
    notify('Rewrite downloaded');
  };

  return (
    <section className="workspace-section dash-studio" aria-labelledby="workspace-title">
      <div className="workspace-heading">
        <div>
          <span className="eyebrow">HUMANIZER</span>
          <h2 id="workspace-title">Make this draft sound like you.</h2>
        </div>
        <span className="demo-pill"><span /> {usage ? `${usage.available_uses} uses left` : 'Your writing studio'}</span>
      </div>
      <div className="workspace-card">
        <div className="workspace-toolbar">
          <div className="control-group">
            <span className="control-label">Voice</span>
            <div className="chip-row" role="group" aria-label="Choose writing voice">
              {(['natural', 'academic', 'simple', 'confident'] as Tone[]).map((item) => (
                <button key={item} className={`tone-chip ${tone === item ? 'active' : ''}`} onClick={() => setTone(item)}>{item[0].toUpperCase() + item.slice(1)}</button>
              ))}
            </div>
          </div>
          <div className="control-group intensity-group">
            <span className="control-label">Rewrite strength</span>
            <div className="segmented" role="group" aria-label="Rewrite strength">
              {(['light', 'balanced', 'deep'] as Strength[]).map((item) => (
                <button key={item} className={strength === item ? 'active' : ''} onClick={() => setStrength(item)}>{item[0].toUpperCase() + item.slice(1)}</button>
              ))}
            </div>
          </div>
          <label className="guard-toggle">
            <span className="guard-icon" aria-hidden="true">✦</span>
            <span><strong>{guard ? 'Meaning Guard' : 'Style focus'}</strong><small>{guard ? 'Protect facts & citations' : 'Prioritise flow'}</small></span>
            <input type="checkbox" checked={guard} onChange={(event) => setGuard(event.target.checked)} />
            <span className="switch" aria-hidden="true" />
          </label>
        </div>
        <div className="editor-grid">
          <article className="editor-panel input-panel">
            <div className="panel-head">
              <div><span className="panel-kicker">ORIGINAL DRAFT</span><span className={`unsaved-dot ${source ? 'visible' : ''}`} /></div>
              <div className="panel-actions">
                <button className="icon-text-btn" onClick={() => setSource(sample)}>✦ Sample</button>
                <button className="round-btn" aria-label="Clear text" onClick={() => { setSource(''); setResult(''); setStatus('Ready'); }}>×</button>
              </div>
            </div>
            <div className="textarea-wrap">
              <textarea maxLength={5000} aria-label="Original draft" placeholder="Paste your essay, report, email, or any AI-assisted draft here…" value={source} onChange={(event) => setSource(event.target.value)} />
              {!source && <div className="paste-hint"><span aria-hidden="true">Aa</span><span>Paste your draft here<br /><small>Up to 5,000 characters</small></span></div>}
            </div>
            <div className="panel-foot"><span>{sourceMeta}</span><span>English</span></div>
          </article>
          <div className="editor-divider" aria-hidden="true"><span>→</span></div>
          <article className="editor-panel output-panel">
            <div className="panel-head">
              <div><span className="panel-kicker">PHRASIO REWRITE</span><span className={`status-badge ${status === 'Complete' ? 'done' : ''}`}>{status}</span></div>
              <div className="panel-actions">
                <button className="icon-text-btn" disabled={!result} onClick={() => void copyResult()}>□ Copy</button>
                <button className="round-btn" aria-label="Download output" disabled={!result} onClick={downloadResult}>↓</button>
              </div>
            </div>
            <div className={`output-content ${!result && !loading ? 'empty' : ''} ${loading ? 'loading' : ''}`} tabIndex={0}>
              {!result && !loading && (
                <div className="empty-state">
                  <div className="empty-orbit"><span>✦</span></div>
                  <strong>Your rewrite will appear here</strong>
                  <p>Add a draft, choose your voice, and let Phrasio smooth the rough edges.</p>
                </div>
              )}
              <p id="resultText">{result}</p>
              {loading && <div className="loading-state" aria-live="polite"><span className="loading-line long" /><span className="loading-line medium" /><span className="loading-line long" /><span className="loading-line short" /></div>}
            </div>
            <div className="panel-foot output-foot"><span>{resultMeta}</span><span>{changeMeta}</span></div>
          </article>
        </div>
        <div className="workspace-bottom">
          <div className="guard-preview">
            <span className="mini-shield" aria-hidden="true">{guard ? '✓' : '—'}</span>
            <span>
              <strong>{guard ? 'Meaning Guard is on' : 'Meaning Guard is off'}</strong>
              <small>{guard ? 'Names, numbers, citations, and key claims will be checked.' : 'The rewrite will prioritise style and flow.'}</small>
            </span>
          </div>
          <button className={`button button-primary humanize-btn ${loading ? 'is-loading' : ''}`} onClick={() => void runHumanize()} disabled={loading}>
            <span>{loading ? 'Finding your voice…' : result ? 'Humanize again' : 'Humanize draft'}</span>
            <b aria-hidden="true">✦</b>
          </button>
        </div>
      </div>
    </section>
  );
}
